// ============================================================================
//  BiometricPages.tsx — Interface Pointage biométrique (terminal ZKTeco iFace3)
//   · Import du fichier *_attlog.dat (dépôt clé USB)
//   · Association employé ↔ zkUserId
//   · Tableau de bord du jour (+ alerte import périmé)
//   · Vue mensuelle + correction manuelle + recalcul
//
//  N'écrit rien côté pointage : réutilise l'API attendance existante (qui pose
//  isManuallyEdited=true) et l'API biometric backend. QR/team clock non touchés.
// ============================================================================

import { useState, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Fingerprint, Upload, Link2, AlertTriangle, Calendar, CheckCircle,
  RefreshCw, Save, X, Pencil, FileWarning, Loader2, Lock, UserX, ArrowRight,
  Server, Trash2, MapPin, Power,
} from 'lucide-react'
import { biometricApi, type BiometricImportResult, type BiometricDevice } from '@/modules/biometric/api/biometric.api'
import { hrApi, type DailyOverviewCounts } from '@/modules/hr/api/hr.api'
import { Tabs } from '@/components/ui'
import { usePermission } from '@/modules/auth/hooks/usePermission'
import { useNotificationStore } from '@/stores/notification.store'

// Miroir de la constante backend (biometric-compute.service.ts).
const INCOMPLETE_DAY_NOTE = 'Journée incomplète — sortie manquante (source biométrique)'
const STALE_IMPORT_DAYS = 3

// ─── Helpers ─────────────────────────────────────────────────────────────────

const Spinner = () => <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('fr-SN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) : '—'
/** Les punches sont en UTC (terminal Sénégal UTC+0) : on affiche l'heure UTC telle quelle. */
const fmtTimeUtc = (d?: string | null) =>
  d ? new Date(d).toLocaleTimeString('fr-SN', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) : '—'
const shortId = (id?: string | null) => id ? id.slice(0, 8) + '…' : '—'
const minToHhmm = (m?: number | null) => (m || m === 0) ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : '—'

/** ISO instant → valeur d'un <input datetime-local> en UTC (Sénégal UTC+0). */
const toLocalInput = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`
}
/** Valeur <input datetime-local> (UTC) → ISO instant. */
const fromLocalInput = (val: string) => val ? new Date(`${val}:00.000Z`).toISOString() : undefined

const isIncomplete = (a: any) => !a?.clockOut && typeof a?.notes === 'string' && a.notes.startsWith('Journée incomplète')

/** Référence stable tant que la liste n'est pas chargée (évite d'invalider les mémos). */
const NO_DEVICES: BiometricDevice[] = []

/** Résout un n° de série de terminal en libellé lisible « Nom (SN) ». */
function useDeviceLabels() {
  const { data: devices } = useQuery({
    queryKey: ['biometric-devices'],
    queryFn:  () => biometricApi.listDevices(),
  })
  const bySn = useMemo(() => {
    const m = new Map<string, string>()
    ;(devices ?? []).forEach(d => m.set(d.deviceSn, d.name))
    return m
  }, [devices])
  const deviceLabel = (sn?: string | null) => {
    if (!sn) return '—'
    const name = bySn.get(sn)
    return name ? `${name} (${sn})` : sn
  }
  return { devices: devices ?? NO_DEVICES, deviceLabel }
}

const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  PRESENT:  { label: 'Présent',      color: 'text-[#059669]',      dot: 'bg-[#10B981]' },
  LATE:     { label: 'En retard',    color: 'text-[#D97706]',      dot: 'bg-[#F59E0B]' },
  HALF_DAY: { label: 'Demi-journée', color: 'text-[var(--blue-d)]', dot: 'bg-[#38BDF8]' },
  ON_LEAVE: { label: 'En congé',     color: 'text-[#7C3AED]',      dot: 'bg-[#8B5CF6]' },
  ABSENT:   { label: 'Absent',       color: 'text-[var(--tx3)]',   dot: 'bg-[#D6D2EC]' },
}

// ============================================================================
//  1. IMPORT (écran principal)
// ============================================================================

export function BiometricImportPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [result, setResult] = useState<BiometricImportResult | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const { deviceLabel } = useDeviceLabels()
  const { data: history, isLoading: histLoading } = useQuery({
    queryKey: ['biometric-imports'],
    queryFn:  () => biometricApi.listImports(),
  })

  const importM = useMutation({
    mutationFn: (f: File) => biometricApi.importAttlog(f),
    onSuccess: (res) => {
      setResult(res); setErrorMsg(null); setFile(null)
      qc.invalidateQueries({ queryKey: ['biometric-imports'] })
      qc.invalidateQueries({ queryKey: ['biometric-unmatched'] })
      qc.invalidateQueries({ queryKey: ['attendanceOverview'] })
      addNotification({ type: 'success', title: 'Import terminé', message: `${res.linesNew} nouveau(x) pointage(s).` })
    },
    onError: (e: any) => {
      // Message backend tel quel (ex. refus d'un gabarit biométrique).
      setResult(null)
      setErrorMsg(e?.response?.data?.message ?? 'Import impossible.')
    },
  })

  const pickFile = (f?: File | null) => { if (f) { setFile(f); setResult(null); setErrorMsg(null) } }

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center gap-3">
        <Fingerprint className="w-6 h-6 text-[var(--brand-dark)]" />
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Import badgeuse</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Déposez le fichier <code className="font-mono">&lt;numéro-de-série&gt;_attlog.dat</code> exporté du terminal.</p>
        </div>
      </div>

      {/* Zone d'upload */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]) }}
          onClick={() => fileInputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-3 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
            dragOver ? 'border-[var(--blue)] bg-[#F0F4FF]' : 'border-[var(--bd)] hover:border-[var(--blue)] hover:bg-[var(--surface-2)]'
          }`}
        >
          <Upload className="w-8 h-8 text-[var(--tx3)]" />
          {file ? (
            <p className="text-sm text-[var(--brand-dark)] font-medium">{file.name}</p>
          ) : (
            <>
              <p className="text-sm text-[var(--tx2)] font-medium">Glissez le fichier ici, ou cliquez pour parcourir</p>
              <p className="text-xs text-[var(--tx3)]">Fichier attendu : <span className="font-mono">*_attlog.dat</span> — les gabarits biométriques sont refusés</p>
            </>
          )}
          <input ref={fileInputRef} type="file" accept=".dat" className="hidden"
            onChange={e => { pickFile(e.target.files?.[0]); e.target.value = '' }} />
        </div>

        {file && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <button onClick={() => setFile(null)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
            <button
              disabled={importM.isPending}
              onClick={() => importM.mutate(file)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-50"
            >
              {importM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importM.isPending ? 'Import en cours…' : 'Importer'}
            </button>
          </div>
        )}
      </div>

      {/* Erreur backend (message tel quel, ex. gabarit refusé) */}
      {errorMsg && (
        <div className="bg-[#FEF2F2] border border-[rgba(239,68,68,0.35)] rounded-xl p-4 flex items-start gap-3">
          <FileWarning className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#DC2626]">Import refusé</p>
            <p className="text-sm text-[#991B1B] mt-0.5">{errorMsg}</p>
          </div>
        </div>
      )}

      {/* Récapitulatif de l'import */}
      {result && <ImportSummary result={result} />}

      {/* Historique */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--bd)]">
          <h2 className="text-sm font-semibold text-[var(--brand-dark)]">Historique des imports</h2>
        </div>
        {histLoading ? (
          <div className="flex items-center justify-center py-10"><Spinner /></div>
        ) : !history?.length ? (
          <div className="py-10 text-center text-[var(--tx3)] text-sm">Aucun import pour le moment.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Date', 'Fichier', 'Terminal', 'Lues', 'Nouvelles', 'Période', 'Auteur'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {history.map(imp => (
                <tr key={imp.id} className="hover:bg-[var(--surface-2)] transition-colors">
                  <td className="px-4 py-3 text-[var(--tx2)]">{fmtDateTime(imp.importedAt)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--brand-dark)]">{imp.fileName}</td>
                  <td className="px-4 py-3 text-xs text-[var(--tx3)]">{deviceLabel(imp.deviceSn)}</td>
                  <td className="px-4 py-3 text-[var(--tx2)]">{imp.linesRead}</td>
                  <td className="px-4 py-3 font-semibold text-[var(--brand-dark)]">{imp.linesNew}</td>
                  <td className="px-4 py-3 text-xs text-[var(--tx3)]">{fmtDateTime(imp.firstPunchAt)} → {fmtTimeUtc(imp.lastPunchAt)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--tx3)]">{shortId(imp.importedById)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function ImportSummary({ result }: { result: BiometricImportResult }) {
  const { deviceLabel } = useDeviceLabels()
  const cards = [
    { label: 'Lignes lues',        value: result.linesRead },
    { label: 'Nouveaux pointages', value: result.linesNew, accent: true },
    { label: 'Employés concernés', value: result.recompute?.employeesProcessed ?? '—' },
    { label: 'Journées recalculées', value: result.recompute?.daysComputed ?? '—' },
  ]
  return (
    <div className="space-y-4">
      {/* ALERTE DE DÉRIVE D'HORLOGE — très visible */}
      {result.driftAlert && (
        <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-[#B45309] shrink-0" />
          <div>
            <p className="text-sm font-bold text-[#92400E]">⚠ Dérive d'horloge du terminal détectée</p>
            <p className="text-sm text-[#92400E] mt-0.5">
              {result.driftMessage ?? `Écart de ${result.clockDriftMinutes} min entre le dernier pointage et l'heure serveur.`}
            </p>
            <p className="text-xs text-[#B45309] mt-1">
              Vérifiez l'heure du terminal avant la paie : un décalage non corrigé produit des retards fantômes.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle className="w-5 h-5 text-[#059669]" />
          <h2 className="text-sm font-semibold text-[var(--brand-dark)]">Import terminé — {deviceLabel(result.deviceSn)}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map(c => (
            <div key={c.label} className={`rounded-xl p-4 text-center border ${c.accent ? 'bg-[#ECFDF5] border-[#10B981]/40' : 'bg-[var(--bg)] border-[var(--bd)]'}`}>
              <p className={`text-2xl font-bold ${c.accent ? 'text-[#059669]' : 'text-[var(--brand-dark)]'}`}>{c.value}</p>
              <p className="text-xs text-[var(--tx3)] mt-0.5">{c.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3 text-sm">
          <div><span className="text-[var(--tx3)]">Période : </span><span className="text-[var(--brand-dark)]">{fmtDateTime(result.period.firstPunchAt)} → {fmtTimeUtc(result.period.lastPunchAt)}</span></div>
          <div><span className="text-[var(--tx3)]">Badges vus : </span><span className="text-[var(--brand-dark)]">{result.distinctZkUserIdCount}</span></div>
          <div><span className="text-[var(--tx3)]">Lignes ignorées (non reconnues) : </span><span className="text-[var(--brand-dark)]">{result.droppedUnknownUser}</span></div>
          <div><span className="text-[var(--tx3)]">Lignes illisibles : </span><span className="text-[var(--brand-dark)]">{result.malformed}</span></div>
        </div>

        {/* zkUserId inconnus */}
        {result.unknownZkUserIds.length > 0 && (
          <div className="mt-4 bg-[#FFF7ED] border border-[#FB923C]/40 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <UserX className="w-4 h-4 text-[#C2410C]" />
              <p className="text-sm font-semibold text-[#9A3412]">Badges non associés à un employé ({result.unknownZkUserIds.length})</p>
            </div>
            <p className="text-xs text-[#9A3412] mb-2">
              Ces pointages sont bien enregistrés mais ne produiront aucune journée tant qu'ils ne sont pas associés.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {result.unknownZkUserIds.map(z => (
                <span key={z} className="font-mono text-xs bg-white border border-[#FB923C]/40 text-[#9A3412] px-2 py-0.5 rounded">{z}</span>
              ))}
            </div>
            <Link to="/hr/biometric/associations" className="inline-flex items-center gap-1 text-sm text-[var(--blue-d)] hover:underline font-medium">
              Associer ces badges <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  2. ASSOCIATION employé ↔ zkUserId
// ============================================================================

export function BiometricAssociationsPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const { deviceLabel, devices } = useDeviceLabels()
  const [selection, setSelection] = useState<Record<string, string>>({})   // "deviceSn|zkUserId" → employeeId
  const [editing, setEditing] = useState<{ employeeId: string; name: string } | null>(null)
  const [editDevice, setEditDevice] = useState('')
  const [editValue, setEditValue] = useState('')

  const { data: unmatched, isLoading: unmatchedLoading } = useQuery({
    queryKey: ['biometric-unmatched'],
    queryFn:  () => biometricApi.listUnmatched(),
  })
  const { data: empData, isLoading: empLoading } = useQuery({
    queryKey: ['employees-active-biometric'],
    queryFn:  () => hrApi.employees.list({ status: 'ACTIVE', limit: 1000 }),
  })
  const employees: any[] = empData?.employees ?? []

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['biometric-unmatched'] })
    qc.invalidateQueries({ queryKey: ['employees-active-biometric'] })
  }

  const assignM = useMutation({
    mutationFn: ({ employeeId, zkDeviceSn, zkUserId }: { employeeId: string; zkDeviceSn: string; zkUserId: string }) =>
      biometricApi.assignZkUserId(employeeId, zkDeviceSn, zkUserId),
    onSuccess: () => { addNotification({ type: 'success', title: 'Badge associé' }); setEditing(null); refresh() },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message ?? 'Association impossible' }),
  })

  const employeesWithoutBadge = useMemo(
    () => employees.filter(e => e.status === 'ACTIVE' && !e.zkUserId),
    [employees],
  )

  // Terminaux ACTIFS déclarés — seule source autorisée pour l'association (pas de
  // saisie libre : un SN inexistant crée une panne silencieuse).
  const activeDevices = useMemo(() => devices.filter(d => d.isActive), [devices])

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center gap-3">
        <Link2 className="w-6 h-6 text-[var(--brand-dark)]" />
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Associations badges</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Reliez chaque badge du terminal à un employé Pulse.</p>
        </div>
      </div>

      {/* Employés actifs sans badge — ils ne seront jamais pointés */}
      {!empLoading && employeesWithoutBadge.length > 0 && (
        <div className="bg-[#FFF7ED] border border-[#FB923C]/40 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#C2410C]" />
            <p className="text-sm font-semibold text-[#9A3412]">{employeesWithoutBadge.length} employé(s) actif(s) sans badge — jamais pointé(s)</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {employeesWithoutBadge.map(e => (
              <span key={e.id} className="text-xs bg-white border border-[#FB923C]/40 text-[#9A3412] px-2 py-0.5 rounded">
                {e.firstName} {e.lastName} <span className="font-mono opacity-70">{e.matricule}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Badges non associés */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--bd)]">
          <h2 className="text-sm font-semibold text-[var(--brand-dark)]">Badges non associés</h2>
        </div>
        {unmatchedLoading ? (
          <div className="flex items-center justify-center py-10"><Spinner /></div>
        ) : !unmatched?.length ? (
          <div className="py-10 text-center text-[var(--tx3)] text-sm">Tous les badges pointés sont associés. 🎉</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Terminal', 'Badge (n°)', 'Pointages', 'Dernier pointage', 'Associer à', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {unmatched.map(u => {
                const key = `${u.deviceSn}|${u.zkUserId}`
                return (
                  <tr key={key} className="hover:bg-[var(--surface-2)] transition-colors">
                    <td className="px-4 py-3 text-xs text-[var(--tx3)]">{deviceLabel(u.deviceSn)}</td>
                    <td className="px-4 py-3 font-mono text-sm font-semibold text-[var(--brand-dark)]">{u.zkUserId}</td>
                    <td className="px-4 py-3 text-[var(--tx2)]">{u.punchCount}</td>
                    <td className="px-4 py-3 text-xs text-[var(--tx3)]">{fmtDateTime(u.lastPunchAt)}</td>
                    <td className="px-4 py-3">
                      <select
                        value={selection[key] ?? ''}
                        onChange={e => setSelection(s => ({ ...s, [key]: e.target.value }))}
                        className="w-full max-w-xs px-3 py-1.5 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
                      >
                        <option value="">— Sélectionner un employé —</option>
                        {employees.map(e => (
                          <option key={e.id} value={e.id} disabled={!!e.zkUserId}>
                            {e.firstName} {e.lastName} ({e.matricule}){e.zkUserId ? ` — ${e.zkDeviceSn ?? '?'}/${e.zkUserId}` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        disabled={!selection[key] || assignM.isPending}
                        onClick={() => assignM.mutate({ employeeId: selection[key], zkDeviceSn: u.deviceSn, zkUserId: u.zkUserId })}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-40"
                      >
                        Associer
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Employés & badge (visible + modifiable) */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--bd)]">
          <h2 className="text-sm font-semibold text-[var(--brand-dark)]">Employés actifs & badge associé</h2>
        </div>
        {empLoading ? (
          <div className="flex items-center justify-center py-10"><Spinner /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Employé', 'Matricule', 'Terminal', 'Badge (n°)', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {employees.map(e => (
                <tr key={e.id} className="hover:bg-[var(--surface-2)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[var(--brand-dark)]">{e.firstName} {e.lastName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--tx3)]">{e.matricule}</td>
                  <td className="px-4 py-3 text-xs text-[var(--tx3)]">{e.zkDeviceSn ? deviceLabel(e.zkDeviceSn) : '—'}</td>
                  <td className="px-4 py-3">
                    {e.zkUserId
                      ? <span className="font-mono text-sm text-[var(--brand-dark)]">{e.zkUserId}</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-[#C2410C]"><AlertTriangle className="w-3 h-3" /> non associé</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { setEditing({ employeeId: e.id, name: `${e.firstName} ${e.lastName}` }); setEditDevice(e.zkDeviceSn ?? ''); setEditValue(e.zkUserId ?? '') }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-[var(--tx2)] hover:bg-white border border-[var(--bd)] transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" /> {e.zkUserId ? 'Modifier' : 'Associer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modale édition du badge d'un employé */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[var(--brand-dark)]">Badge de {editing.name}</h3>
            {activeDevices.length === 0 ? (
              <p className="text-sm text-[#9A3412] bg-[#FFF7ED] border border-[#FB923C]/40 rounded-lg p-3">
                Aucun terminal connu — importez d'abord un fichier de pointages pour déclarer un terminal.
              </p>
            ) : (
              <>
                <div>
                  <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Terminal</label>
                  <select
                    value={editDevice}
                    onChange={e => setEditDevice(e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] bg-white focus:outline-none focus:border-[var(--blue)]"
                  >
                    <option value="">— Sélectionner un terminal —</option>
                    {activeDevices.map(d => (
                      <option key={d.id} value={d.deviceSn}>{d.name} ({d.deviceSn})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Numéro (zkUserId)</label>
                  <input
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    placeholder="Ex. 1"
                    className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
                  />
                  <p className="text-xs text-[var(--tx3)] mt-1">Identité biométrique = le COUPLE (terminal, numéro) affiché sur le ZKTeco.</p>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
              <button
                disabled={!editValue.trim() || !editDevice.trim() || assignM.isPending}
                onClick={() => assignM.mutate({ employeeId: editing.employeeId, zkDeviceSn: editDevice.trim(), zkUserId: editValue.trim() })}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  3. TABLEAU DE BORD DU JOUR (+ alerte import périmé)
// ============================================================================

/** Identifiant de l'onglet « Tous » (ne peut pas entrer en collision avec un n° de série). */
const ALL_DEVICES_TAB = '__all__'
const ZERO_COUNTS: DailyOverviewCounts = { total: 0, present: 0, onLeave: 0, absent: 0, late: 0 }

export function BiometricDashboardPage() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  // Onglet terminal : état local, conservé quand la date change.
  const [deviceTab, setDeviceTab] = useState<string>(ALL_DEVICES_TAB)
  const { can } = usePermission()
  const { devices } = useDeviceLabels()

  const { data: overview, isLoading, isError, refetch } = useQuery({
    queryKey: ['attendanceOverview', date],
    queryFn:  () => hrApi.attendance.getDailyOverview(date),
  })
  const { data: imports } = useQuery({
    queryKey: ['biometric-imports'],
    queryFn:  () => biometricApi.listImports(),
  })

  const lastImportAt = imports?.length ? imports[0].importedAt : null
  const daysSinceImport = lastImportAt ? Math.floor((Date.now() - new Date(lastImportAt).getTime()) / 86400000) : null
  const importStale = daysSinceImport === null || daysSinceImport >= STALE_IMPORT_DAYS

  // Nom du terminal par n° de série (toute la liste, actifs ou non).
  const deviceNameBySn = useMemo(() => new Map(devices.map(d => [d.deviceSn, d.name])), [devices])

  // Compteurs serveur par terminal (le groupe null = non rattachés, sans onglet dédié).
  const countsBySn = useMemo(() => {
    const m = new Map<string, DailyOverviewCounts>()
    ;(overview?.byDevice ?? []).forEach(g => { if (g.deviceSn) m.set(g.deviceSn, g) })
    return m
  }, [overview?.byDevice])

  // Onglets : terminaux actifs + SN rencontrés chez les employés mais inconnus de la liste.
  const deviceTabs = useMemo(() => {
    const entries = devices
      .filter(d => d.isActive)
      .map(d => ({ id: d.deviceSn, label: d.name?.trim() || d.deviceSn }))
    const knownSn = new Set(devices.map(d => d.deviceSn))
    const orphanSn = new Set<string>()
    ;(overview?.employees ?? []).forEach(e => {
      if (e.zkDeviceSn && !knownSn.has(e.zkDeviceSn)) orphanSn.add(e.zkDeviceSn)
    })
    orphanSn.forEach(sn => entries.push({ id: sn, label: sn }))
    entries.sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base', numeric: true }))
    const hasByDevice = !!overview?.byDevice
    return [
      { id: ALL_DEVICES_TAB, label: 'Tous', count: overview?.summary?.present },
      ...entries.map(t => ({ ...t, count: hasByDevice ? (countsBySn.get(t.id)?.present ?? 0) : undefined })),
    ]
  }, [devices, overview?.employees, overview?.byDevice, overview?.summary?.present, countsBySn])

  // Si le terminal choisi disparaît (désactivé, autre date…), on affiche « Tous » sans perdre le choix.
  const activeTab = deviceTabs.some(t => t.id === deviceTab) ? deviceTab : ALL_DEVICES_TAB
  const isAll = activeTab === ALL_DEVICES_TAB
  const showTabs = deviceTabs.length > 1

  const kpis: DailyOverviewCounts | undefined = isAll
    ? overview?.summary
    : (overview ? countsBySn.get(activeTab) ?? ZERO_COUNTS : undefined)

  const rows = useMemo(
    () => (overview?.employees ?? []).filter(e => isAll || e.zkDeviceSn === activeTab),
    [overview?.employees, isAll, activeTab],
  )
  const columns = isAll
    ? ['Employé', 'Terminal', 'Entrée', 'Sortie', 'Temps', 'Statut']
    : ['Employé', 'Entrée', 'Sortie', 'Temps', 'Statut']

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Fingerprint className="w-6 h-6 text-[var(--brand-dark)]" />
          <div>
            <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Pointage biométrique</h1>
            <p className="text-sm text-[var(--tx3)] mt-0.5">Présence du jour issue du terminal</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} aria-label="Date du pointage"
            className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none" />
          {can('biometric:import') && (
            <Link to="/hr/biometric/import" className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors">
              <Upload className="w-4 h-4" /> Importer
            </Link>
          )}
          <Link to="/hr/biometric/monthly" className="flex items-center gap-2 px-4 py-2 bg-white border border-[var(--bd)] hover:bg-[#F0F4FF] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors">
            <Calendar className="w-4 h-4" /> Vue mensuelle
          </Link>
        </div>
      </div>

      {/* BANDEAU import périmé (> 3 jours) */}
      {importStale && (
        <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-[#B45309] shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-[#92400E]">
              {lastImportAt
                ? `Dernier import il y a ${daysSinceImport} jour(s) — le pointage n'est plus à jour`
                : 'Aucun import biométrique enregistré'}
            </p>
            <p className="text-sm text-[#92400E] mt-0.5">
              Déposez le fichier du terminal pour tenir la présence à jour. {lastImportAt && `Dernier : ${fmtDateTime(lastImportAt)}.`}
            </p>
          </div>
          {can('biometric:import') && (
            <Link to="/hr/biometric/import" className="shrink-0 flex items-center gap-2 px-3 py-2 bg-[#B45309] hover:bg-[#92400E] text-white rounded-lg text-sm font-medium transition-colors">
              <Upload className="w-4 h-4" /> Importer
            </Link>
          )}
        </div>
      )}

      {/* Onglets par terminal (affichés dès qu'au moins un terminal existe) */}
      {showTabs && (
        <Tabs
          tabs={deviceTabs}
          active={activeTab}
          onChange={setDeviceTab}
          ariaLabel="Pointages par terminal"
          idPrefix="biometric-device"
          countLabel="présents"
        />
      )}

      <div
        {...(showTabs ? {
          role: 'tabpanel',
          id: 'biometric-device-panel',
          'aria-labelledby': `biometric-device-tab-${activeTab}`,
          tabIndex: 0,
        } : {})}
        className="space-y-5 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)] focus-visible:ring-offset-2"
      >
      {/* Stats du jour (globales ou du terminal sélectionné) */}
      {kpis && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: 'Total',     value: kpis.total,   color: 'text-[var(--brand-dark)]' },
            { label: 'Présents',  value: kpis.present, color: 'text-[#059669]' },
            { label: 'En congé',  value: kpis.onLeave, color: 'text-[#7C3AED]' },
            { label: 'Absents',   value: kpis.absent,  color: 'text-[var(--tx3)]' },
            { label: 'En retard', value: kpis.late,    color: 'text-[#D97706]' },
          ].map(k => (
            <div key={k.label} className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
              <p className="text-xs text-[var(--tx3)] mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table présence */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-10"><Spinner /></div>
        ) : isError ? (
          <div className="py-10 text-center text-sm space-y-3">
            <p className="text-[var(--tx2)]">Impossible de charger la présence du jour.</p>
            <button type="button" onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] bg-white border border-[var(--bd)] hover:bg-[#F0F4FF] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors">
              <RefreshCw className="w-4 h-4" /> Réessayer
            </button>
          </div>
        ) : !rows.length ? (
          <div className="py-10 text-center text-[var(--tx3)] text-sm">
            {isAll ? 'Aucun employé.' : 'Aucun employé rattaché à ce terminal.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {columns.map(h => (
                  <th key={h} scope="col" className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {rows.map(emp => {
                const cfg = STATUS_CONFIG[emp.status] ?? STATUS_CONFIG.ABSENT
                const a: any = emp.attendance
                const sn = emp.zkDeviceSn ?? null
                const devName = sn ? deviceNameBySn.get(sn) : undefined
                return (
                  <tr key={emp.id} className="hover:bg-[var(--surface-2)] transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-[var(--brand-dark)]">{emp.firstName} {emp.lastName}</p>
                      <p className="text-xs text-[var(--tx3)] font-mono">{emp.matricule}</p>
                    </td>
                    {isAll && (
                      <td className="px-4 py-3">
                        {!sn ? (
                          <span className="text-[var(--tx3)]">
                            <span aria-hidden="true">—</span><span className="sr-only">Aucun terminal</span>
                          </span>
                        ) : devName ? (
                          <>
                            <p className="text-sm text-[var(--tx2)] whitespace-nowrap">{devName}</p>
                            <p className="text-xs text-[var(--tx3)] font-mono">{sn}</p>
                          </>
                        ) : (
                          <span className="text-xs text-[var(--tx2)] font-mono">{sn}</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-[var(--tx2)]">
                      {fmtTimeUtc(a?.clockIn)}
                      {a?.lateMinutes > 0 && <span className="ml-1 text-xs text-[#D97706]">+{a.lateMinutes}min</span>}
                    </td>
                    <td className="px-4 py-3 text-[var(--tx2)]">
                      {a?.clockOut ? fmtTimeUtc(a.clockOut) : isIncomplete(a) ? <span className="text-[#D97706] text-xs">incomplète</span> : '—'}
                    </td>
                    <td className="px-4 py-3 text-[var(--brand-dark)]">{minToHhmm(a?.totalMinutes)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {emp.leave ? emp.leave.leaveType?.name ?? 'En congé' : cfg.label}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
      </div>
    </div>
  )
}

// ============================================================================
//  4. VUE MENSUELLE + CORRECTION MANUELLE + RECALCUL
// ============================================================================

export function BiometricMonthlyPage() {
  const qc = useQueryClient()
  const { can } = usePermission()
  const { addNotification } = useNotificationStore()
  const canEdit = can('attendance:edit')

  const now = new Date()
  const [employeeId, setEmployeeId] = useState('')
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [edit, setEdit] = useState<any | null>(null)
  const [form, setForm] = useState({ clockIn: '', clockOut: '', reason: '' })

  const { data: empData } = useQuery({
    queryKey: ['employees-active-biometric'],
    queryFn:  () => hrApi.employees.list({ status: 'ACTIVE', limit: 1000 }),
  })
  const employees: any[] = empData?.employees ?? []

  const { data: report, isLoading } = useQuery({
    queryKey: ['biometric-monthly', employeeId, year, month],
    queryFn:  () => hrApi.attendance.getMonthlyReport(employeeId, year, month),
    enabled:  !!employeeId,
  })

  const editM = useMutation({
    mutationFn: () => hrApi.attendance.edit(edit.id, {
      clockIn:  fromLocalInput(form.clockIn),
      clockOut: form.clockOut ? fromLocalInput(form.clockOut) : undefined,
      notes:    form.reason.trim(),
    }),
    onSuccess: () => {
      addNotification({ type: 'success', title: 'Journée corrigée', message: 'La correction ne sera plus écrasée par un recalcul.' })
      setEdit(null)
      qc.invalidateQueries({ queryKey: ['biometric-monthly'] })
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message ?? 'Correction impossible' }),
  })

  const recomputeM = useMutation({
    mutationFn: () => {
      const from = `${year}-${String(month).padStart(2, '0')}-01`
      const to = new Date(Date.UTC(year, month, 0)).toISOString().split('T')[0]
      return biometricApi.recompute(from, to)
    },
    onSuccess: (r) => {
      addNotification({ type: 'success', title: 'Recalcul terminé', message: `${r.daysComputed} journée(s), ${r.skippedManual} correction(s) préservée(s).` })
      qc.invalidateQueries({ queryKey: ['biometric-monthly'] })
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message ?? 'Recalcul impossible' }),
  })

  const openEdit = (a: any) => {
    setEdit(a)
    setForm({ clockIn: toLocalInput(a.clockIn), clockOut: toLocalInput(a.clockOut), reason: '' })
  }

  const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
  const attendances: any[] = report?.attendances ?? []

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center gap-3">
        <Calendar className="w-6 h-6 text-[var(--brand-dark)]" />
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Pointage mensuel</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Journées, retards et corrections par employé</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-[var(--bd)] rounded-xl p-4 shadow-sm">
        <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}
          className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]">
          <option value="">— Choisir un employé —</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.matricule})</option>)}
        </select>
        <select value={month} onChange={e => setMonth(Number(e.target.value))}
          className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]">
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" value={year} onChange={e => setYear(Number(e.target.value))}
          className="w-24 px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
        {canEdit && (
          <button
            disabled={recomputeM.isPending}
            onClick={() => recomputeM.mutate()}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-white border border-[var(--bd)] hover:bg-[#F0F4FF] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors disabled:opacity-50"
            title="Relance le calcul des journées biométriques du mois (les corrections manuelles sont préservées)"
          >
            {recomputeM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Recalculer le mois
          </button>
        )}
      </div>

      {!employeeId ? (
        <div className="py-16 text-center text-[var(--tx3)] text-sm bg-[var(--bg)] border border-[var(--bd)] rounded-xl">
          Choisissez un employé pour afficher ses journées.
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <>
          {/* Stats mensuelles */}
          {report?.stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Jours présent', value: report.stats.daysPresent },
                { label: 'Jours en retard', value: report.stats.daysLate },
                { label: 'Heures totales', value: report.stats.totalHours + 'h' },
                { label: 'Journées incomplètes', value: attendances.filter(isIncomplete).length, warn: true },
              ].map(k => (
                <div key={k.label} className={`rounded-xl p-4 text-center border ${k.warn && Number(k.value) > 0 ? 'bg-[#FFF7ED] border-[#FB923C]/40' : 'bg-[var(--bg)] border-[var(--bd)]'}`}>
                  <p className={`text-2xl font-bold ${k.warn && Number(k.value) > 0 ? 'text-[#C2410C]' : 'text-[var(--brand-dark)]'}`}>{k.value}</p>
                  <p className="text-xs text-[var(--tx3)] mt-0.5">{k.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Table journées */}
          <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
            {!attendances.length ? (
              <div className="py-10 text-center text-[var(--tx3)] text-sm">Aucune journée enregistrée sur ce mois.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--bd)]">
                    {['Date', 'Entrée', 'Sortie', 'Temps', 'Retard', 'Statut', 'Source', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--bd)]">
                  {attendances.map(a => {
                    const cfg = STATUS_CONFIG[a.status] ?? STATUS_CONFIG.ABSENT
                    const incomplete = isIncomplete(a)
                    return (
                      <tr key={a.id} className={`hover:bg-[var(--surface-2)] transition-colors ${incomplete ? 'bg-[#FFF7ED]/50' : ''}`}>
                        <td className="px-4 py-3 text-[var(--brand-dark)]">{fmtDate(a.date)}</td>
                        <td className="px-4 py-3 text-[var(--tx2)]">{fmtTimeUtc(a.clockIn)}</td>
                        <td className="px-4 py-3 text-[var(--tx2)]">
                          {a.clockOut ? fmtTimeUtc(a.clockOut)
                            : incomplete ? <span className="inline-flex items-center gap-1 text-xs text-[#C2410C]"><AlertTriangle className="w-3 h-3" /> incomplète</span>
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-[var(--brand-dark)]">{minToHhmm(a.totalMinutes)}</td>
                        <td className="px-4 py-3 text-[var(--tx2)]">{a.lateMinutes > 0 ? <span className="text-[#D97706]">+{a.lateMinutes}min</span> : '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-[var(--tx3)]">{a.source}</span>
                            {a.isManuallyEdited && (
                              <span title="Corrigée manuellement — ne sera plus écrasée par un recalcul"
                                className="inline-flex items-center gap-1 text-[10px] font-medium text-[#7C3AED] bg-[#EDE9FE] px-1.5 py-0.5 rounded">
                                <Lock className="w-3 h-3" /> corrigée
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {canEdit && (
                            <button onClick={() => openEdit(a)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-[var(--tx2)] hover:bg-white border border-[var(--bd)] transition-colors">
                              <Pencil className="w-3.5 h-3.5" /> Corriger
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Modale correction manuelle */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-lg space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[var(--brand-dark)]">Corriger la journée du {fmtDate(edit.date)}</h3>
              <button onClick={() => setEdit(null)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"><X className="w-4 h-4" /></button>
            </div>

            <div className="bg-[#EDE9FE] border border-[#8B5CF6]/30 rounded-lg p-3 flex items-start gap-2">
              <Lock className="w-4 h-4 text-[#7C3AED] shrink-0 mt-0.5" />
              <p className="text-xs text-[#5B21B6]">Une fois corrigée, cette journée est verrouillée : elle ne sera plus jamais écrasée par un recalcul biométrique.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Entrée (heure Sénégal)</label>
                <input type="datetime-local" value={form.clockIn} onChange={e => setForm(f => ({ ...f, clockIn: e.target.value }))}
                  className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Sortie (laisser vide si absente)</label>
                <input type="datetime-local" value={form.clockOut} onChange={e => setForm(f => ({ ...f, clockOut: e.target.value }))}
                  className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Justification (obligatoire)</label>
              <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Motif de la correction (oubli de badge, sortie non pointée…)"
                rows={3}
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
              <button
                disabled={!form.clockIn || form.reason.trim().length < 3 || editM.isPending}
                onClick={() => editM.mutate()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-50"
              >
                {editM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer la correction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  5. GESTION DES TERMINAUX (BiometricDevice)
// ============================================================================

export function BiometricDevicesPage() {
  const qc = useQueryClient()
  const { can } = usePermission()
  const { addNotification } = useNotificationStore()
  const canEdit = can('attendance:edit')

  const { data: devices, isLoading, isError } = useQuery({
    queryKey: ['biometric-devices'],
    queryFn:  () => biometricApi.listDevices(),
  })

  const [editing, setEditing] = useState<BiometricDevice | null>(null)
  const [form, setForm] = useState({ name: '', location: '' })

  const toastErr = (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message ?? 'Une erreur est survenue' })
  const refresh  = () => qc.invalidateQueries({ queryKey: ['biometric-devices'] })

  const saveM = useMutation({
    mutationFn: () => biometricApi.updateDevice(editing!.id, { name: form.name.trim(), location: form.location.trim() || null }),
    onSuccess: () => { addNotification({ type: 'success', title: 'Terminal mis à jour' }); setEditing(null); refresh() },
    onError: toastErr,
  })
  const toggleM = useMutation({
    mutationFn: (d: BiometricDevice) => biometricApi.updateDevice(d.id, { isActive: !d.isActive }),
    onSuccess: () => { refresh() },
    onError: toastErr,
  })
  const deleteM = useMutation({
    mutationFn: (id: string) => biometricApi.deleteDevice(id),
    onSuccess: () => { addNotification({ type: 'success', title: 'Terminal supprimé' }); refresh() },
    onError: toastErr,   // le backend refuse si des pointages y sont rattachés (message affiché)
  })

  const openEdit = (d: BiometricDevice) => { setEditing(d); setForm({ name: d.name, location: d.location ?? '' }) }

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center gap-3">
        <Server className="w-6 h-6 text-[var(--brand-dark)]" />
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Terminaux de pointage</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Nommez vos terminaux ZKTeco pour les reconnaître d'un coup d'œil.</p>
        </div>
      </div>

      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : isError ? (
          <div className="py-12 text-center text-[var(--tx3)] text-sm">Impossible de charger les terminaux.</div>
        ) : !devices?.length ? (
          <div className="py-12 text-center">
            <Server className="w-8 h-8 text-[var(--tx3)] mx-auto mb-3" />
            <p className="text-[var(--tx3)] text-sm">Aucun terminal. Il sera créé automatiquement au premier import.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Nom', 'N° de série', 'Lieu', 'Statut', 'Dernier import', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {devices.map(d => (
                <tr key={d.id} className={`hover:bg-[var(--surface-2)] transition-colors ${d.isActive ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 font-medium text-[var(--brand-dark)]">{d.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--tx3)]">{d.deviceSn}</td>
                  <td className="px-4 py-3 text-[var(--tx2)]">{d.location || '—'}</td>
                  <td className="px-4 py-3">
                    {d.isActive
                      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-[#059669] bg-[#10B981]/15 px-2 py-0.5 rounded-full">Actif</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--tx3)] bg-[#D6D2EC]/20 px-2 py-0.5 rounded-full">Inactif</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--tx3)]">{fmtDateTime(d.lastImportAt)}</td>
                  <td className="px-4 py-3 text-right">
                    {canEdit && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(d)} title="Renommer / lieu"
                          className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-white border border-[var(--bd)] transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => toggleM.mutate(d)} title={d.isActive ? 'Désactiver' : 'Activer'} disabled={toggleM.isPending}
                          className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[#D97706] hover:bg-white border border-[var(--bd)] transition-colors disabled:opacity-50">
                          <Power className="w-4 h-4" />
                        </button>
                        <button
                          disabled={deleteM.isPending}
                          onClick={() => { if (window.confirm(`Supprimer le terminal « ${d.name} » ?`)) deleteM.mutate(d.id) }}
                          title="Supprimer"
                          className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[#DC2626] hover:bg-white border border-[var(--bd)] transition-colors disabled:opacity-50">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modale renommer / lieu */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[var(--brand-dark)]">Terminal {editing.deviceSn}</h3>
              <button onClick={() => setEditing(null)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Nom</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex. Bureau Liberté 1"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Lieu</label>
              <div className="relative">
                <MapPin className="w-4 h-4 text-[var(--tx3)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="Ex. Restaurant Point E"
                  className="w-full pl-9 pr-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
              <button disabled={!form.name.trim() || saveM.isPending} onClick={() => saveM.mutate()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-50">
                {saveM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
